import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigationType, useSearchParams } from 'react-router';
import type { SnapshotFrom } from 'xstate';
import { UserContext, useConfirmationView } from '~/app';
import { Button } from '~/components/buttons';
import { LoadingSpinner } from '~/components/LoadingSpinner';
import { useFeatureFlag, useFeatureFlagStatus } from '~/features/feature-flags/useFeatureFlag';
import { isNotificationArrival } from '~/features/notifications/constants';
import { useDeepLinkedResponseSelection } from '~/features/shared/deepLinkParams';
import type { MarketFact } from '~/features/shared/market-content';
import {
  MarketActionRow,
  MarketAlert,
  MarketContentPanel,
  MarketEmptyPanel,
  MarketFactGrid,
  MarketLoadingPanel,
  MarketPanelHeader,
  MarketPartySummary,
  MarketResponseListItem,
  MarketSegmentedControl,
  MarketStatusPill,
  MarketTurnPill,
  MarketWorkspaceGrid,
  formatResponseStatusLabel,
  formatStatusLabel,
} from '~/features/shared/market-content';
import { MessageComposer } from '~/features/shared/market-content/MessageComposer';
import {
  ResponseUnmaskPanel,
  selectUnmaskViewerScope,
} from '~/features/shared/market-content/ResponseUnmaskPanel';
import { OpportunityFilters } from '~/features/shared/OpportunityFilters';
import { OpportunityRecoveryNotice } from '~/features/shared/OpportunityRecoveryNotice';
import type { receivedResponseActionsMachine } from '~/features/shared/receivedResponseActions.machine';
import {
  isReceivedResponseVisible,
  reconcileReceivedResponseRows,
} from '~/features/shared/receivedResponseRows';
import { declineResponseConfirmation } from '~/features/shared/responseCloseConfirmations';
import {
  ReceivedResponseActionsBoundary,
  useReceivedResponseActions,
} from '~/features/shared/useReceivedResponseActions';
import { useResponseDeclinedToast } from '~/features/shared/useResponseDeclinedToast';
import { extractBackendErrorDetail } from '~/features/utils';
import { withRequestDeadline } from '~/utils/requestDeadline';
import { b2bSideCopy } from '../b2bSideCopy';
import { ContractReadyNotice } from '../contracts/ContractReadyNotice';
import { API_CALLS } from '../job-opportunities/api';
import { JobOpportunities } from '../job-opportunities/JobOpportunities';
import { B2BNegotiationHistory } from '../job-opportunities/sub-components/B2BNegotiationHistory';
import { ListingFilledNotice } from '../job-opportunities/sub-components/ListingFilledNotice';
import { MessagesSection } from '../job-opportunities/sub-components/MessagesSection';
import { ResponseProvidersSection } from '../job-opportunities/sub-components/ResponseProvidersSection';
import type {
  B2BListingResponse,
  B2BMessage,
  B2BOpportunityFilter,
  B2BResponsesSide,
} from '../job-opportunities/types';
import {
  LISTING_FILLED_LABEL,
  formatDate,
  formatHourlyRate,
  formatRelativeTime,
  formatWorkLocation,
  isListingFilled,
} from '../job-opportunities/utils';
import {
  FEE_AUTHORIZATION_REQUIRED_MESSAGE,
  FEE_SCHEDULE_CHANGED_MESSAGE,
} from '../listing-search/errors';
import { ResponseNegotiationModal } from '../negotiations/ResponseNegotiationModal';
import { ActivationFeeDialog } from '../negotiations/sub-components/ActivationFeeDialog';
import { parseRememberedSide, readRememberedSide, storeRememberedSide } from '../rememberedSide';
import { MARKET_READ_TIMEOUT_MS } from '../requestPolicy';
import { useB2BAcceptFee } from '../useB2BAcceptFee';

type WorkspaceView = 'received' | 'sent';

const WORKSPACE_VIEW_OPTIONS: Array<{
  label: string;
  value: WorkspaceView;
  cyTag: string;
}> = [
  { label: 'Received responses', value: 'received', cyTag: 'b2b-responses-view-received' },
  { label: 'Sent responses', value: 'sent', cyTag: 'b2b-responses-view-sent' },
];

const SIDE_OPTIONS: Array<{ label: string; value: B2BResponsesSide; cyTag: string }> = [
  { label: 'Buying', value: 'buying', cyTag: 'b2b-responses-side-buying' },
  { label: 'Selling', value: 'selling', cyTag: 'b2b-responses-side-selling' },
];

const INVALID_LISTING_FILTER_MESSAGE = 'Invalid listing filter; showing all responses.';
const MISSING_LISTING_FILTER_MESSAGE = 'Filter listing not found; showing all responses.';

const parseView = (value: string | null): WorkspaceView => (value === 'sent' ? 'sent' : 'received');

const getErrorMessage = (err: unknown, fallback: string) => {
  const response =
    err != null && typeof err === 'object' && 'response' in err
      ? (err as { response?: { status?: number; data?: { detail?: unknown } } }).response
      : undefined;
  const backendDetail = extractBackendErrorDetail(response?.data?.detail);

  if (response?.status === 409) {
    return backendDetail ?? "This listing isn't ready for negotiation.";
  }

  return backendDetail ?? (err instanceof Error ? err.message : fallback);
};

type ReceivedQuery = {
  listingId?: number;
  filter: B2BOpportunityFilter;
  includeDeclined: boolean;
  warning: string | null;
};

const hasListingMatch = (rows: B2BListingResponse[], listingId: number) =>
  rows.some((row) => row.listing.id === listingId);

const getResponseStatusTone = (
  status: string
): Parameters<typeof MarketStatusPill>[0]['status'] => {
  if (status === 'ACCEPTED') return 'accepted';
  if (status === 'REJECTED') return 'danger';
  if (status === 'WITHDRAWN') return 'inactive';
  if (status === 'NEGOTIATING' || status === 'PENDING_ACCEPTANCE') return 'pending';
  return 'neutral';
};

// TODO(MCV-817 follow-up): split this workspace per
// aidocs/backlog/split-b2b-opportunities-workspace.md.
export const B2BOpportunitiesWorkspace = () => {
  // [MCV-1889] The flags read false until they load, so the page would mount flag-off, read side-less,
  // then remount with a side (and the first mount could consume a deep link). Wait for them instead.
  const flagStatus = useFeatureFlagStatus();
  if (flagStatus === 'loading') return <LoadingSpinner />;
  return <LoadedB2BOpportunitiesWorkspace />;
};

const LoadedB2BOpportunitiesWorkspace = () => {
  const viewerScopeKey = useSelector(UserContext.useActorRef(), selectUnmaskViewerScope);
  const offeringEnabled = useFeatureFlag('b2bOfferingListings');
  const [searchParams, setSearchParams] = useSearchParams();
  const storedSide = useMemo(
    () => readRememberedSide('responses', viewerScopeKey),
    [viewerScopeKey]
  );
  const view = parseView(searchParams.get('view'));
  // [MCV-1889] With the flag off there is no side at all, so every request is exactly today's.
  // With it on, ?side= wins, then the tab a side-less link to one response always meant (Received
  // is Buying, Sent is Selling), then the last tab used, then Buying (Buying + Received is today's
  // Received list).
  const linkedSide: B2BResponsesSide | null = searchParams.has('responseId')
    ? view === 'sent'
      ? 'selling'
      : 'buying'
    : null;
  const side: B2BResponsesSide | undefined = offeringEnabled
    ? (parseRememberedSide(searchParams.get('side')) ?? linkedSide ?? storedSide ?? 'buying')
    : undefined;
  const listingIdParam = searchParams.get('listingId');
  const parsedListingId = listingIdParam ? Number(listingIdParam) : undefined;
  const listingId =
    parsedListingId != null && Number.isInteger(parsedListingId) && parsedListingId > 0
      ? parsedListingId
      : undefined;
  const listingFilterWarning =
    listingIdParam != null && listingId == null ? INVALID_LISTING_FILTER_MESSAGE : null;
  const setView = (nextView: WorkspaceView) => {
    const params = new URLSearchParams(searchParams);
    params.set('view', nextView);
    // Keep a side inferred from a deep link, which goes with the responseId removed below.
    if (side) params.set('side', side);
    if (nextView === 'sent') {
      params.delete('listingId');
    }
    // A deep-linked response belongs to the view it was linked into. Leaving it
    // in the URL would have the other view hunt for a row it cannot contain.
    params.delete('responseId');
    setSearchParams(params, { replace: true });
  };

  const setSide = (nextSide: B2BResponsesSide) => {
    storeRememberedSide('responses', viewerScopeKey, nextSide);
    const params = new URLSearchParams(searchParams);
    params.set('side', nextSide);
    // A deep-linked response or listing filter belongs to the tab it was linked into.
    params.delete('responseId');
    params.delete('listingId');
    setSearchParams(params, { replace: true });
  };

  return (
    <ReceivedResponseActionsBoundary>
      <div className="flex min-h-0 flex-1 flex-col" data-cy="b2b-opportunities-workspace">
        {side && (
          <div className="mb-3 flex shrink-0">
            <MarketSegmentedControl
              className="w-full sm:w-auto"
              options={SIDE_OPTIONS}
              value={side}
              onChange={setSide}
            />
          </div>
        )}
        <div className="mb-4 flex shrink-0">
          <MarketSegmentedControl
            className="w-full sm:w-auto"
            options={WORKSPACE_VIEW_OPTIONS}
            value={view}
            onChange={setView}
          />
        </div>

        {view === 'sent' ? (
          <JobOpportunities
            // The Sent list is read once per side, so each tab gets its own list state.
            key={side}
            title={side ? 'Sent responses' : 'Sent B2B Opportunities'}
            side={side}
          />
        ) : (
          <B2BReceivedOpportunities
            key={`${viewerScopeKey}:${side ?? ''}`}
            viewerScopeKey={viewerScopeKey}
            side={side}
            listingId={listingId}
            listingFilterWarning={listingFilterWarning}
          />
        )}
      </div>
    </ReceivedResponseActionsBoundary>
  );
};

// [MCV-1549] This reads the deep link itself rather than taking it as a prop.
// react-router keeps the workspace mounted when only the query string changes,
// so a prop threaded from a value the parent captured once would make every
// notification after the first a no-op — and would hand a sent-view arrival's
// (absent) id to this component when it mounts on the received view.
const B2BReceivedOpportunities = ({
  viewerScopeKey,
  side,
  listingId,
  listingFilterWarning: initialListingFilterWarning,
}: {
  viewerScopeKey: string;
  side?: B2BResponsesSide;
  listingId?: number;
  listingFilterWarning: string | null;
}) => {
  const userRef = UserContext.useActorRef();
  const userInfoId = useSelector(userRef, (s) => s.context.userInfoId);
  const [filter, setFilter] = useState<B2BOpportunityFilter>('all');
  // [MCV-1850] Declined and withdrawn responses are hidden unless asked for.
  const [includeDeclined, setIncludeDeclined] = useState(false);
  const [responses, setResponses] = useState<B2BListingResponse[]>([]);
  const [selectedResponseId, setSelectedResponseId] = useState<number | null>(null);
  // [MCV-1889] DD22: below 1360px the list and the opened response are separate panes. Only a row
  // the user opens (or a deep link) switches panes; the first row selected on load does not.
  const [detailOpen, setDetailOpen] = useState(false);
  const openResponse = useCallback((responseId: number) => {
    setSelectedResponseId(responseId);
    setDetailOpen(true);
  }, []);
  const [loading, setLoading] = useState(false);
  const listReadRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      listReadRef.current?.abort();
    };
  }, []);
  const [negotiationResponseId, setNegotiationResponseId] = useState<number | null>(null);
  const [negotiationStartDraftIfEmpty, setNegotiationStartDraftIfEmpty] = useState(false);
  const confirmationView = useConfirmationView();
  const [error, setError] = useState<string | null>(null);
  const [listingFilterWarning, setListingFilterWarning] = useState<string | null>(
    initialListingFilterWarning
  );
  const [reloadNonce, setReloadNonce] = useState(0);
  const recoveryRef =
    useRef<SnapshotFrom<typeof receivedResponseActionsMachine>['context']['refreshed']>(null);

  // [MCV-1549] A ref write triggers no render and no request. The linked row
  // may be newer than this list, or hidden by the active status filter, and
  // neither listingId nor filter necessarily changed — so reset to 'all' (a
  // filtered query cannot return a row the filter excludes) and bump the nonce
  // so an already-'all' view still refetches.
  const requestDeepLinkReload = useCallback(() => {
    setFilter('all');
    setReloadNonce((nonce) => nonce + 1);
  }, []);

  const { consumePendingResponseId, peekPendingResponseId } = useDeepLinkedResponseSelection({
    rows: responses,
    onSelect: openResponse,
    onRequestReload: requestDeepLinkReload,
  });

  // A pending deep link wins; otherwise the selection survives if it still
  // exists; otherwise the first row. The link has to outrank the survivor
  // because this reload was very likely requested because of it, and the user's
  // previous row usually still exists — keeping it would silently discard the
  // notification they just clicked.
  const selectAfterLoad = useCallback(
    (rows: B2BListingResponse[]) => {
      const requested = consumePendingResponseId(rows);
      // A linked response opens its detail pane too (below 1360px), not just the selection.
      if (requested != null) setDetailOpen(true);
      setSelectedResponseId((current) => {
        if (requested != null) return requested;
        if (current != null && rows.some((row) => row.responseId === current)) {
          return current;
        }
        return rows[0]?.responseId ?? null;
      });
    },
    [consumePendingResponseId]
  );

  // [MCV-1549] The effect's `active` cleanup only covers loads the effect
  // started. An action awaited on listing A resumes through refreshResponses
  // after the user has navigated to B, starting a *separate* request with A's
  // captured inputs that the cleanup never sees. What makes a result valid is
  // that its inputs still match the current ones.
  // The warning is part of the identity: no `listingId` and an invalid one such
  // as `?listingId=abc` both normalise to `{ listingId: undefined }`, so without
  // it a refresh captured under one URL is accepted under the other.
  const currentQueryRef = useRef<ReceivedQuery>({
    listingId,
    filter,
    includeDeclined,
    warning: initialListingFilterWarning,
  });
  currentQueryRef.current = {
    listingId,
    filter,
    includeDeclined,
    warning: initialListingFilterWarning,
  };
  const isCurrentQuery = useCallback(
    (query: ReceivedQuery) =>
      currentQueryRef.current.listingId === query.listingId &&
      currentQueryRef.current.filter === query.filter &&
      currentQueryRef.current.includeDeclined === query.includeDeclined &&
      currentQueryRef.current.warning === query.warning,
    []
  );

  // [MCV-1549] Pure: it returns the warning rather than setting it. It used to
  // write `listingFilterWarning` at four points, and to throw after writing —
  // all before the caller's stale-query check, so a refresh whose *rows* were
  // correctly rejected could still replace the current listing's warning, or
  // surface the stale request's error on it. Returning the warning lets the
  // caller apply rows, warning and error together, after one validation.
  const loadListingResponses = useCallback(
    async (
      signal: AbortSignal
    ): Promise<{
      rows: B2BListingResponse[];
      warning: string | null;
    }> => {
      let rows = await API_CALLS.getListingResponses({
        filter,
        listingId,
        includeDeclined,
        side,
        signal,
      });
      signal.throwIfAborted();
      if (listingId != null && rows.length === 0) {
        let listingRows = rows;
        // Whether the listing exists at all is judged on every status, so a listing whose
        // only responses are declined is not reported as missing.
        if (filter !== 'all' || !includeDeclined) {
          listingRows = await API_CALLS.getListingResponses({
            filter: 'all',
            listingId,
            includeDeclined: true,
            side,
            signal,
          });
          signal.throwIfAborted();
        }

        if (hasListingMatch(listingRows, listingId)) {
          return { rows, warning: initialListingFilterWarning };
        }

        rows = await API_CALLS.getListingResponses({
          filter,
          listingId: undefined,
          includeDeclined,
          side,
          signal,
        });
        return { rows, warning: MISSING_LISTING_FILTER_MESSAGE };
      }

      return { rows, warning: initialListingFilterWarning };
    },
    [filter, includeDeclined, initialListingFilterWarning, listingId, side]
  );

  const refreshResponses = useCallback(
    async ({ showLoading = false }: { showLoading?: boolean } = {}) => {
      const query = { listingId, filter, includeDeclined, warning: initialListingFilterWarning };
      const recoveryAtStart = recoveryRef.current;
      // A stale action callback must not cancel the newer route's request.
      if (!mountedRef.current || !isCurrentQuery(query)) return;
      listReadRef.current?.abort();
      const controller = new AbortController();
      listReadRef.current = controller;
      const isCurrent = () =>
        !controller.signal.aborted && listReadRef.current === controller && isCurrentQuery(query);
      if (showLoading) setLoading(true);
      setError(null);
      try {
        const { rows, warning, widened } = await withRequestDeadline(
          async (signal) => {
            const loaded = await loadListingResponses(signal);
            // [MCV-1850] The linked response may be declined or withdrawn, which the default
            // list hides. Look again with those included, and keep the checkbox in step.
            const pendingResponseId = peekPendingResponseId();
            if (
              pendingResponseId != null &&
              !includeDeclined &&
              !loaded.rows.some((row) => row.responseId === pendingResponseId)
            ) {
              const wider = await API_CALLS.getListingResponses({
                filter,
                listingId:
                  loaded.warning === MISSING_LISTING_FILTER_MESSAGE ? undefined : listingId,
                includeDeclined: true,
                side,
                signal,
              });
              signal.throwIfAborted();
              if (wider.some((row) => row.responseId === pendingResponseId))
                return { ...loaded, rows: wider, widened: true };
            }
            return { ...loaded, widened: false };
          },
          MARKET_READ_TIMEOUT_MS,
          controller.signal
        );
        if (!isCurrent()) return;
        setListingFilterWarning(warning);
        const latest = recoveryRef.current;
        const currentRows =
          latest?.workspace === 'b2b' && latest !== recoveryAtStart
            ? reconcileReceivedResponseRows(
                rows,
                latest.action,
                latest.response,
                filter,
                includeDeclined
              )
            : rows;
        setResponses(currentRows);
        selectAfterLoad(currentRows);
        if (widened) setIncludeDeclined(true);
      } catch (err) {
        if (!isCurrent()) return;
        setListingFilterWarning(initialListingFilterWarning);
        setError(err instanceof Error ? err.message : 'Failed to load responses');
        throw err;
      } finally {
        if (isCurrent()) setLoading(false);
      }
    },
    [
      filter,
      includeDeclined,
      initialListingFilterWarning,
      isCurrentQuery,
      listingId,
      loadListingResponses,
      peekPendingResponseId,
      selectAfterLoad,
    ]
  );

  useEffect(() => {
    void refreshResponses({ showLoading: true }).catch(() => undefined);
    return () => listReadRef.current?.abort();
    // reloadNonce is the deep-link reload trigger: the target may be missing
    // without listingId or filter having changed, so nothing else re-runs this.
  }, [refreshResponses, reloadNonce]);

  const selected = useMemo(
    () => responses.find((row) => row.responseId === selectedResponseId) ?? null,
    [responses, selectedResponseId]
  );
  const actions = useReceivedResponseActions(
    selected
      ? {
          workspace: 'b2b',
          listingId: selected.listing.id,
          responseId: selected.responseId,
          expectedNegotiationId: selected.latestNegotiation?.id,
          side,
        }
      : null
  );
  const negotiationResponse = useMemo(
    () => responses.find((row) => row.responseId === negotiationResponseId),
    [responses, negotiationResponseId]
  );
  const { messages, messagesError, messagesLoading, refreshed } = actions;
  // [MCV-1672] The receiving employer's Accept previews the activation fee first, and only opens
  // the confirmation dialog when this viewer is actually billed. The preview is for the selected
  // row: the sidebar stays selectable while it is in flight.
  const {
    requestAccept,
    previewing: previewingActivation,
    dialogProps: activationDialogProps,
  } = useB2BAcceptFee({
    selectedResponseId,
    onAccept: (_payload, feeAuthorization) => actions.write('accept', undefined, feeAuthorization),
  });
  useResponseDeclinedToast(actions.receipt);
  if (refreshed) recoveryRef.current = refreshed;

  // [MCV-1753] A notification click on the response already open reloads the row (the deep-link
  // protocol above refetches the list on every navigation), but the open conversation is keyed on
  // the response and nothing re-reads its messages, so a new message stayed missing. The key of
  // that click re-reads them. It is latched as in useContractsService: it only moves for a NEW
  // click, never on mount (the thread loads fresh) and never on a POP or a plain navigation.
  const location = useLocation();
  const navigationType = useNavigationType();
  const arrivalKey = isNotificationArrival(location.state, navigationType) ? location.key : null;
  const [seenArrivalKey, setSeenArrivalKey] = useState(arrivalKey);
  const [notificationReloads, setNotificationReloads] = useState(0);
  if (arrivalKey !== null && arrivalKey !== seenArrivalKey) {
    setSeenArrivalKey(arrivalKey);
    setNotificationReloads((count) => count + 1);
  }
  const reloadMessagesRef = useRef(actions.reloadMessages);
  reloadMessagesRef.current = actions.reloadMessages;
  useEffect(() => {
    if (notificationReloads > 0) reloadMessagesRef.current();
  }, [notificationReloads]);
  useEffect(() => {
    if (refreshed?.workspace !== 'b2b') return;
    const { filter: queryFilter, includeDeclined: queryIncludeDeclined } = currentQueryRef.current;
    setResponses((current) =>
      reconcileReceivedResponseRows(
        current,
        refreshed.action,
        refreshed.response,
        queryFilter,
        queryIncludeDeclined
      )
    );
    // A row that left the list (withdrawn, or declined with the checkbox off) clears the
    // detail pane rather than leaving it on a response the list no longer shows.
    if (!isReceivedResponseVisible(refreshed.response, queryFilter, queryIncludeDeclined))
      setSelectedResponseId((current) =>
        current === refreshed.action.responseId ? null : current
      );
  }, [refreshed]);

  // [MCV-1646] The negotiation editor opens immediately in draft mode; no
  // request is made until Submit Offer.
  const handleCounter = useCallback(() => {
    if (!selected || actions.blocked) return;
    setNegotiationStartDraftIfEmpty(selected.latestNegotiation == null);
    setNegotiationResponseId(selected.responseId);
  }, [selected, actions.blocked]);

  const runAccept = useCallback(
    async (notice: string | null) => {
      if (!selected || actions.blocked) return;
      await requestAccept(selected.responseId, undefined, notice);
    },
    [selected, actions.blocked, requestAccept]
  );
  const handleAccept = useCallback(() => runAccept(null), [runAccept]);

  // [MCV-1858] The server refused the accept for its platform-fee schedule (428 not authorized,
  // 409 changed since the preview). Refetch the schedule and show it again with the reason.
  const runAcceptRef = useRef(runAccept);
  runAcceptRef.current = runAccept;
  const { feeRefusal, consumeFeeRefusal } = actions;
  useEffect(() => {
    if (!feeRefusal) return;
    consumeFeeRefusal();
    void runAcceptRef.current(
      feeRefusal === 'FEE_SCHEDULE_CHANGED'
        ? FEE_SCHEDULE_CHANGED_MESSAGE
        : FEE_AUTHORIZATION_REQUIRED_MESSAGE
    );
  }, [feeRefusal, consumeFeeRefusal]);

  const handleNegotiationClose = useCallback(async () => {
    setNegotiationResponseId(null);
    try {
      await refreshResponses();
    } catch (err) {
      console.error('Failed to refresh responses after negotiation close', err);
      setError(getErrorMessage(err, 'Failed to refresh responses.'));
    }
  }, [refreshResponses]);

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-cy="b2b-received-opportunities">
      {error && <MarketAlert tone="error">{error}</MarketAlert>}
      {actions.error && !actions.feeRefusal && (
        <MarketAlert tone="error">{actions.error}</MarketAlert>
      )}
      <OpportunityRecoveryNotice
        action={actions.uncertain}
        busy={actions.busy}
        canAcknowledge={actions.canAcknowledge}
        onReview={() => {
          setFilter('all');
          actions.review();
        }}
        onAcknowledge={actions.acknowledge}
        onDiscard={actions.discard}
        discardsDraft
      />
      {actions.readError && !actions.uncertain && (
        <MarketAlert tone="error">
          {actions.readError}
          <p>The write was acknowledged. Dismissing this warning does not resend or undo it.</p>
          <div className="mt-2 flex flex-wrap gap-3">
            <Button
              label="Dismiss refresh warning"
              disabled={actions.busy}
              onClick={actions.discard}
            />
            <Button
              label="Refresh original response"
              disabled={actions.busy}
              onClick={() => {
                setFilter('all');
                actions.review();
              }}
            />
          </div>
        </MarketAlert>
      )}
      {messagesError && (
        <MarketAlert tone="error">
          {messagesError}
          <Button type="button" size="sm" label="Retry messages" onClick={actions.reloadMessages} />
        </MarketAlert>
      )}
      {listingFilterWarning && <MarketAlert tone="warning">{listingFilterWarning}</MarketAlert>}
      <MarketWorkspaceGrid
        dataCy="b2b-received-responses-grid"
        detailKey={selected?.responseId}
        drillDown={side ? { detailOpen, onBack: () => setDetailOpen(false) } : undefined}
        sidebar={
          <ReceivedSidebar
            responsiveFilters={side != null}
            responses={responses}
            selectedResponseId={selected?.responseId ?? null}
            filter={filter}
            includeDeclined={includeDeclined}
            loading={loading}
            onFilter={setFilter}
            onIncludeDeclined={setIncludeDeclined}
            onSelect={openResponse}
          />
        }
        detail={
          selected ? (
            <ReceivedDetail
              response={selected}
              onAccept={handleAccept}
              onCounter={handleCounter}
              onReject={() =>
                confirmationView.send(declineResponseConfirmation(() => actions.write('reject')))
              }
              actionsDisabled={loading || actions.blocked || previewingActivation}
              pinActions={side != null}
              identityControls={
                <ResponseUnmaskPanel
                  responseId={selected.responseId}
                  viewerScopeKey={viewerScopeKey}
                  refreshKey={`${selected.updatedAt}:${messages[messages.length - 1]?.id ?? ''}`}
                  disabled={loading || actions.blocked}
                  onChanged={() => {
                    setReloadNonce((nonce) => nonce + 1);
                    actions.reloadMessages();
                  }}
                />
              }
            />
          ) : (
            <EmptyPanel label={loading ? 'Loading responses...' : 'No received responses'} />
          )
        }
        conversation={
          selected ? (
            <ReceivedConversation
              key={selected.responseId}
              response={selected}
              messages={messages}
              currentUserInfoId={userInfoId}
              loading={messagesLoading}
              sending={actions.blocked}
              error={actions.replyError}
              draft={actions.draft}
              onDraftChange={actions.edit}
              onSend={(body) => actions.write('reply', body)}
            />
          ) : (
            <EmptyPanel label="No response selected" />
          )
        }
      />
      <ResponseNegotiationModal
        open={negotiationResponseId != null}
        responseId={negotiationResponseId}
        responseType="msaas"
        viewerRole="EMPLOYER"
        listingSide={negotiationResponse?.listingSide}
        listingFilled={negotiationResponse != null && isListingFilled(negotiationResponse)}
        startDraftIfEmpty={negotiationStartDraftIfEmpty}
        onDraftSubmitted={() => {
          void refreshResponses().catch((err) => {
            console.error('Failed to refresh responses after negotiation submit', err);
            setError(getErrorMessage(err, 'Failed to refresh responses.'));
          });
        }}
        onClose={handleNegotiationClose}
      />
      <ActivationFeeDialog {...activationDialogProps} />
    </div>
  );
};

const ReceivedSidebar = ({
  responses,
  selectedResponseId,
  filter,
  includeDeclined,
  loading,
  responsiveFilters,
  onFilter,
  onIncludeDeclined,
  onSelect,
}: {
  responses: B2BListingResponse[];
  selectedResponseId: number | null;
  filter: B2BOpportunityFilter;
  includeDeclined: boolean;
  loading: boolean;
  responsiveFilters?: boolean;
  onFilter: (filter: B2BOpportunityFilter) => void;
  onIncludeDeclined: (includeDeclined: boolean) => void;
  onSelect: (responseId: number) => void;
}) => {
  return (
    <MarketContentPanel
      className="h-full"
      bodyClassName="flex min-h-0 flex-1 flex-col p-2"
      dataCy="b2b-received-responses-sidebar"
      header={
        <>
          <MarketPanelHeader
            title="Received B2B Responses"
            subtitle={`${responses.length} ${responses.length === 1 ? 'response' : 'responses'}`}
          />
          <OpportunityFilters
            options={(['all', 'active', 'accepted'] as const).map((option) => ({
              label: formatStatusLabel(option),
              value: option,
            }))}
            filter={filter}
            onFilter={onFilter}
            includeDeclined={includeDeclined}
            onIncludeDeclined={onIncludeDeclined}
            responsive={responsiveFilters}
          />
        </>
      }
      variant="scroll"
    >
      {loading && <MarketLoadingPanel />}
      {!loading &&
        responses.map((response) => {
          const meta = [
            response.listing.specialtyName,
            formatWorkLocation(response.listing.workLocation),
            response.listing.stateCodes.join(', '),
          ]
            .filter(Boolean)
            .join(' / ');

          return (
            <MarketResponseListItem
              key={response.responseId}
              selected={selectedResponseId === response.responseId}
              title={response.listing.listingTitle}
              subtitle={response.responder.displayName}
              status={
                isListingFilled(response) ? 'neutral' : getResponseStatusTone(response.status)
              }
              statusLabel={
                isListingFilled(response)
                  ? LISTING_FILLED_LABEL
                  : formatResponseStatusLabel(response.status)
              }
              meta={meta}
              timestamp={formatRelativeTime(response.updatedAt)}
              aside={<MarketTurnPill turn={response.turn} />}
              onClick={() => onSelect(response.responseId)}
            />
          );
        })}
      {!loading && responses.length === 0 && (
        <MarketEmptyPanel
          title="No responses match this filter"
          className="min-[1360px]:-translate-y-12"
        />
      )}
    </MarketContentPanel>
  );
};

const ReceivedDetail = ({
  response,
  onAccept,
  onCounter,
  onReject,
  actionsDisabled,
  pinActions,
  identityControls,
}: {
  response: B2BListingResponse;
  onAccept: () => void;
  onCounter: () => void;
  onReject: () => void;
  actionsDisabled?: boolean;
  pinActions?: boolean;
  identityControls?: ReactNode;
}) => {
  const copy = b2bSideCopy(response.listingSide);
  // [MCV-1889] A response closed because its listing filled offers no actions.
  const listingFilled = isListingFilled(response);
  const canAccept = !listingFilled && response.availableActions.includes('accept');
  const canCounter = !listingFilled && response.availableActions.includes('counter');
  const canReject = !listingFilled && response.availableActions.includes('reject');
  const counterLabel = response.latestNegotiation ? 'Counter' : 'Negotiate';
  const pendingAcceptanceMessage =
    response.status !== 'PENDING_ACCEPTANCE'
      ? null
      : response.turn === 'YOUR_TURN'
        ? copy.responderAcceptedNote
        : copy.youAcceptedNote;
  const responseProviders = response.responseProviders ?? [];
  const facts = (
    [
      response.listing.specialtyName
        ? { label: 'Specialty', value: response.listing.specialtyName }
        : null,
      { label: 'States', value: response.listing.stateCodes.join(', ') || '—' },
      { label: 'Work Location', value: formatWorkLocation(response.listing.workLocation) },
      response.listing.numOfFtes != null
        ? { label: 'FTEs', value: String(response.listing.numOfFtes) }
        : null,
      response.listing.pricePerHour != null
        ? { label: 'Rate', value: formatHourlyRate(response.listing.pricePerHour), highlight: true }
        : null,
      response.listing.startDate || response.listing.endDate
        ? {
            label: 'Duration',
            value:
              response.listing.startDate && response.listing.endDate
                ? `${formatDate(response.listing.startDate)} - ${formatDate(response.listing.endDate)}`
                : response.listing.startDate
                  ? `${formatDate(response.listing.startDate)}+`
                  : `Until ${formatDate(response.listing.endDate)}`,
          }
        : null,
    ] as Array<MarketFact | null>
  ).filter((fact): fact is MarketFact => fact !== null);

  return (
    <MarketContentPanel
      className="h-full"
      bodyClassName="flex min-h-0 flex-1 flex-col p-0"
      dataCy="b2b-received-response-detail"
      variant="scroll"
    >
      <div className="shrink-0 border-b border-grey-900 p-4 sm:p-6">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="text-left">
            <MarketStatusPill status="responded" label="Received" />
            <h2 className="sub-heading-sb mt-2 text-content-primary">
              {response.listing.listingTitle}
            </h2>
          </div>
          <MarketTurnPill turn={response.turn} />
        </div>
        <MarketPartySummary
          label={copy.responderLabel}
          name={response.responder.displayName}
          description={response.responder.masked ? 'Identity masked' : 'Identity visible'}
        />
        <MarketFactGrid facts={facts} />
        {listingFilled && <ListingFilledNotice className="mt-5" />}
        {pendingAcceptanceMessage && !listingFilled && (
          <div
            className="mt-5 rounded-lg border border-primary-t-100 bg-primary-t-900/40 p-4 text-left"
            data-cy="b2b-pending-acceptance-notice"
          >
            <h3 className="text-sm font-semibold text-content-primary">Pending acceptance</h3>
            <p className="mt-1 text-sm text-content-secondary">{pendingAcceptanceMessage}</p>
          </div>
        )}
        {/* [MCV-1954] Both parties accepted: the contract is waiting on the Contracts page. */}
        {response.status === 'ACCEPTED' && (
          <div className="mt-5">
            <ContractReadyNotice />
          </div>
        )}
        {(canAccept || canCounter || canReject) && (
          <MarketActionRow
            align="start"
            className="mt-5"
            pinned={pinActions}
            actions={[
              ...(canAccept
                ? [
                    {
                      label: 'Accept',
                      onClick: onAccept,
                      colorVariant: 'primary' as const,
                      disabled: actionsDisabled,
                    },
                  ]
                : []),
              ...(canCounter
                ? [
                    {
                      label: counterLabel,
                      onClick: onCounter,
                      colorVariant: 'secondary' as const,
                      disabled: actionsDisabled,
                    },
                  ]
                : []),
              ...(canReject
                ? [
                    {
                      label: 'Decline',
                      cyTag: CT.marketResponses.b2bDeclineBtn,
                      onClick: onReject,
                      colorVariant: 'error' as const,
                      disabled: actionsDisabled,
                    },
                  ]
                : []),
            ]}
          />
        )}
      </div>
      <div className="shrink-0 p-4 sm:p-6">
        {/* Identity access opens the lower section, below the divider, as on Job Responses and
            Provider Responses. */}
        {identityControls && <div className="mb-5">{identityControls}</div>}
        <ResponseProvidersSection
          responseId={response.responseId}
          providers={responseProviders}
          responseUnmasked={response.isUnmasked}
          listingSide={response.listingSide}
          viewerRole={response.viewerRole}
        />
        <div className="mt-6">
          <B2BNegotiationHistory
            responseId={response.responseId}
            latestNegotiation={response.latestNegotiation}
            negotiationSummaries={response.negotiationSummaries}
            viewerSender="LISTING_OWNER"
          />
        </div>
      </div>
    </MarketContentPanel>
  );
};

const ReceivedConversation = ({
  response,
  messages,
  currentUserInfoId,
  loading,
  sending,
  error,
  draft,
  onDraftChange,
  onSend,
}: {
  response: B2BListingResponse;
  messages: B2BMessage[];
  currentUserInfoId: number | undefined;
  loading: boolean;
  sending: boolean;
  error: string | null;
  draft: string;
  onDraftChange: (message: string) => void;
  onSend: (message: string) => void;
}) => {
  const submit = () => {
    const trimmed = draft.trim();
    if (!trimmed || sending) return;
    onSend(trimmed);
  };
  return (
    <MarketContentPanel
      className="h-full"
      bodyClassName="flex min-h-0 flex-1 flex-col p-0"
      variant="scroll"
    >
      <div className="border-b border-grey-900 p-4 text-left sm:p-5">
        <h3 className="body-text-sb">{response.responder.displayName}</h3>
        <div className="mt-2">
          <MarketStatusPill
            status={response.responder.masked ? 'masked' : 'unmasked'}
            label={response.responder.masked ? 'Identity masked' : 'Responder'}
          />
        </div>
      </div>
      <div className="flex flex-1 flex-col overflow-hidden">
        <MessagesSection
          messages={messages}
          currentUserInfoId={currentUserInfoId}
          loading={loading}
        />
      </div>
      <MessageComposer
        value={draft}
        onChange={onDraftChange}
        onSend={submit}
        error={error}
        disabled={sending}
        sending={sending}
        masked={response.responder.masked}
        label={b2bSideCopy(response.listingSide).messageToResponder}
        sendLabel="Send"
        inputTag="b2b-received-message-input"
        sendTag="btn-send-b2b-received-message"
      />
    </MarketContentPanel>
  );
};

const EmptyPanel = ({ label }: { label: string }) => (
  <MarketContentPanel className="h-full" variant="empty">
    <MarketEmptyPanel title={label} />
  </MarketContentPanel>
);
